import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  LayersOutlined,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SelectElement } from "@types";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { AdminConfigurationNav } from "src/components/AdminConfigurationNav";
import { Select as FormSelect } from "src/components/Form/Elements/Select";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { useSnackbar } from "src/components/Snackbar";
import {
  getAdminRoadmapCardsQueryKey,
} from "src/config/request-keys";
import {
  useGetAdminDevices,
  useGetAdminOrganizations,
  useGetAdminRoadmapCards,
  useGetAdminRoadmapStages,
  useGetUser,
} from "src/hooks";
import {
  configureRoadmapCardDisabledTargetsAdmin,
  createRoadmapCard,
  deleteRoadmapCard,
  updateRoadmapCard,
} from "src/services/backend";
import { RoadmapCard, RoadmapStage } from "src/stores/models";
import { RoadmapVisibilityConditions } from "src/stores/models/roadmap-visibility-conditions";

type StageOption = {
  stageKey: string;
  title: string;
};

type CardGroup = {
  cardKey: string;
  base: RoadmapCard | null;
  overrides: RoadmapCard[];
  cards: RoadmapCard[];
};

const getGroupDisabledTargets = (group: CardGroup) => {
  const organizationIds = new Set<string>();
  const deviceIds = new Set<string>();

  group.cards.forEach((card) => {
    card.disabledForOrganizations?.forEach((organization) => {
      organizationIds.add(organization.id);
    });
    card.disabledForDevices?.forEach((device) => {
      deviceIds.add(device.id);
    });
  });

  return {
    organizationIds: Array.from(organizationIds),
    deviceIds: Array.from(deviceIds),
  };
};

const VISIBILITY_CONDITION_OPTIONS: Array<{
  label: string;
  key: keyof RoadmapVisibilityConditions;
}> = [
    { key: "isMDR", label: "MDR" },
    { key: "isIVDR", label: "IVDR" },
    { key: "isFDA", label: "FDA" },
    { key: "isUK", label: "UK" },
    { key: "isCanada", label: "Canada" },
    { key: "isClass1", label: "Class I" },
    { key: "isClass2a", label: "Class IIa" },
    { key: "isClass2b", label: "Class IIb" },
    { key: "isClass3", label: "Class III" },
    { key: "hasHardware", label: "Hardware" },
    { key: "hasSoftware", label: "Software" },
    { key: "hasElectrical", label: "Electrical" },
    { key: "hasSterile", label: "Sterile" },
    { key: "hasFirmware", label: "Firmware" },
    { key: "hasMechanical", label: "Mechanical" },
    { key: "hasImplantable", label: "Implantable" },
    { key: "isSoftwareOnly", label: "Software-only" },
  ];

const formatScopeLabel = (
  card: RoadmapCard,
  orgNameById: Map<string, string>,
  deviceById: Map<string, { name: string; organizationName?: string | null }>,
) => {
  if (!card.organizationId && !card.deviceId) {
    return "Base (Global)";
  }
  if (card.deviceId) {
    const device = deviceById.get(card.deviceId);
    if (device?.organizationName) {
      return `Device override: ${device.organizationName} — ${device.name}`;
    }
    return "Device override";
  }
  if (card.organizationId) {
    const orgName = orgNameById.get(card.organizationId);
    return orgName ? `Organization override: ${orgName}` : "Organization override";
  }
  return "Override";
};

const mergeStageOptions = (stages: RoadmapStage[]) => {
  return stages.map((stage) => ({
    stageKey: stage.stageKey,
    title: stage.title,
  }));
};

export const AdminRoadmapCards = () => {
  const queryClient = useQueryClient();
  const { orgId = "" } = useParams();
  const { showSnackbar } = useSnackbar();
  const { data: user } = useGetUser();
  const canManage = !!user?.permissions?.includes("write:configuration");

  const { data: adminOrganizations = [] } = useGetAdminOrganizations({
    enabled: canManage,
  });
  const { data: adminDevices = [] } = useGetAdminDevices({
    enabled: canManage,
  });
  const { data: cards = [], isLoading } = useGetAdminRoadmapCards({
    enabled: canManage,
  });

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"base" | "override" | "edit">(
    "base",
  );
  const [editingCard, setEditingCard] = useState<RoadmapCard | null>(null);
  const [overrideBase, setOverrideBase] = useState<RoadmapCard | null>(null);
  const [createScope, setCreateScope] = useState<
    "global" | "organization" | "device"
  >("global");
  const [createOrganizationId, setCreateOrganizationId] = useState<string>("");
  const [createDeviceId, setCreateDeviceId] = useState<string>("");
  const [overrideScope, setOverrideScope] = useState<"organization" | "device">(
    "organization",
  );
  const [overrideOrganizationId, setOverrideOrganizationId] =
    useState<string>("");
  const [overrideDeviceId, setOverrideDeviceId] = useState<string>("");
  const [cardKey, setCardKey] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [helpCenterArticleId, setHelpCenterArticleId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [order, setOrder] = useState<string>("");
  const [parentStageKey, setParentStageKey] = useState("");
  const [visibilityConditions, setVisibilityConditions] =
    useState<RoadmapVisibilityConditions>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RoadmapCard | null>(null);
  const [configureDisabledGroup, setConfigureDisabledGroup] =
    useState<CardGroup | null>(null);
  const [configuredOrganizationIds, setConfiguredOrganizationIds] = useState<
    string[]
  >([]);
  const [configuredDeviceIds, setConfiguredDeviceIds] = useState<string[]>([]);
  const [configureError, setConfigureError] = useState<string | null>(null);

  const orgNameById = useMemo(() => {
    return new Map(adminOrganizations.map((org) => [org.id, org.name]));
  }, [adminOrganizations]);

  const deviceById = useMemo(() => {
    return new Map(
      adminDevices.map((device) => [
        device.id,
        { name: device.name, organizationName: device.organizationName },
      ]),
    );
  }, [adminDevices]);

  const selectedOverrideOrganization = useMemo(() => {
    return (
      adminOrganizations.find((org) => org.id === overrideOrganizationId) ??
      null
    );
  }, [adminOrganizations, overrideOrganizationId]);

  const selectedCreateOrganization = useMemo(() => {
    return adminOrganizations.find((org) => org.id === createOrganizationId) ?? null;
  }, [adminOrganizations, createOrganizationId]);

  const selectedOverrideDevice = useMemo(() => {
    return (
      adminDevices.find((device) => device.id === overrideDeviceId) ?? null
    );
  }, [adminDevices, overrideDeviceId]);

  const selectedCreateDevice = useMemo(() => {
    return adminDevices.find((device) => device.id === createDeviceId) ?? null;
  }, [adminDevices, createDeviceId]);

  const selectedConfiguredOrganizations = useMemo(
    () =>
      adminOrganizations.filter((organization) =>
        configuredOrganizationIds.includes(organization.id),
      ),
    [adminOrganizations, configuredOrganizationIds],
  );

  const selectedConfiguredDevices = useMemo(
    () =>
      adminDevices.filter((device) => configuredDeviceIds.includes(device.id)),
    [adminDevices, configuredDeviceIds],
  );

  const resolvedOverrideOrganizationId =
    overrideScope === "device"
      ? overrideOrganizationId || selectedOverrideDevice?.organizationId || ""
      : overrideOrganizationId;
  const resolvedCreateOrganizationId =
    createScope === "device"
      ? createOrganizationId || selectedCreateDevice?.organizationId || ""
      : createOrganizationId;
  const activeScope =
    dialogMode === "base"
      ? createScope
      : dialogMode === "override"
        ? overrideScope
        : editingCard?.deviceId
          ? "device"
          : editingCard?.organizationId
            ? "organization"
            : "global";
  const activeOrganizationId =
    dialogMode === "base"
      ? resolvedCreateOrganizationId
      : dialogMode === "override"
        ? resolvedOverrideOrganizationId
        : editingCard?.organizationId || "";
  const activeDeviceId =
    dialogMode === "base"
      ? createDeviceId
      : dialogMode === "override"
        ? overrideDeviceId
        : editingCard?.deviceId || "";

  const { data: globalStages = [] } = useGetAdminRoadmapStages({
    scope: "global",
    enabled: canManage && dialogOpen,
  });
  const { data: organizationStages = [] } = useGetAdminRoadmapStages({
    scope: "organization",
    orgId: activeOrganizationId,
    enabled:
      canManage &&
      dialogOpen &&
      activeScope !== "global" &&
      !!activeOrganizationId,
  });
  const { data: deviceStages = [] } = useGetAdminRoadmapStages({
    scope: "device",
    orgId: activeOrganizationId,
    deviceId: activeDeviceId,
    enabled:
      canManage &&
      dialogOpen &&
      activeScope === "device" &&
      !!activeOrganizationId &&
      !!activeDeviceId,
  });

  const stageOptions = useMemo(() => {
    if (activeScope === "global") {
      return mergeStageOptions(globalStages);
    }

    if (activeScope === "organization") {
      const map = new Map<string, StageOption>();
      mergeStageOptions(organizationStages).forEach((stage) => {
        map.set(stage.stageKey, stage);
      });
      mergeStageOptions(globalStages).forEach((stage) => {
        if (!map.has(stage.stageKey)) {
          map.set(stage.stageKey, stage);
        }
      });
      return Array.from(map.values());
    }

    const map = new Map<string, StageOption>();
    mergeStageOptions(deviceStages).forEach((stage) => {
      map.set(stage.stageKey, stage);
    });
    mergeStageOptions(organizationStages).forEach((stage) => {
      if (!map.has(stage.stageKey)) {
        map.set(stage.stageKey, stage);
      }
    });
    mergeStageOptions(globalStages).forEach((stage) => {
      if (!map.has(stage.stageKey)) {
        map.set(stage.stageKey, stage);
      }
    });
    return Array.from(map.values());
  }, [
    activeScope,
    deviceStages,
    globalStages,
    organizationStages,
  ]);

  const selectedStageOption = useMemo(() => {
    return (
      stageOptions.find((stage) => stage.stageKey === parentStageKey) ?? null
    );
  }, [parentStageKey, stageOptions]);

  const groupedCards = useMemo<CardGroup[]>(() => {
    const map = new Map<string, RoadmapCard[]>();
    cards.forEach((card) => {
      const list = map.get(card.cardKey) ?? [];
      list.push(card);
      map.set(card.cardKey, list);
    });
    return Array.from(map.entries()).map(([cardKey, groupCards]) => {
      const base = groupCards.find(
        (card) => !card.organizationId && !card.deviceId,
      );
      const overrides = groupCards
        .filter((card) => card !== base)
        .sort((a, b) => {
          if (a.deviceId && !b.deviceId) return 1;
          if (!a.deviceId && b.deviceId) return -1;
          return a.title.localeCompare(b.title);
        });
      return { cardKey, base: base ?? null, overrides, cards: groupCards };
    });
  }, [cards]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groupedCards;
    const query = search.toLowerCase();
    return groupedCards.filter(
      (group) =>
        group.cardKey.toLowerCase().includes(query) ||
        group.cards.some((card) => card.title.toLowerCase().includes(query)),
    );
  }, [groupedCards, search]);

  const invalidateRoadmapHierarchyQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["roadmapCards", orgId],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ["roadmapStages", orgId],
      exact: false,
    });
  };

  const createMutation = useMutation({
    mutationFn: createRoadmapCard,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getAdminRoadmapCardsQueryKey(),
      });
      await invalidateRoadmapHierarchyQueries();
      handleCloseDialog();
    },
    onError: () => {
      showSnackbar("Failed to save roadmap card.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      cardId,
      data,
    }: {
      cardId: string;
      data: Parameters<typeof updateRoadmapCard>[1];
    }) => updateRoadmapCard(cardId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getAdminRoadmapCardsQueryKey(),
      });
      await invalidateRoadmapHierarchyQueries();
      handleCloseDialog();
    },
    onError: () => {
      showSnackbar("Failed to update roadmap card.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRoadmapCard,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getAdminRoadmapCardsQueryKey(),
      });
      await invalidateRoadmapHierarchyQueries();
      setConfirmDelete(null);
    },
    onError: () => {
      showSnackbar("Failed to delete roadmap card.");
    },
  });

  const configureDisabledTargetsMutation = useMutation({
    mutationFn: async ({
      cardKey,
      organizationIds,
      deviceIds,
    }: {
      cardKey: string;
      organizationIds: string[];
      deviceIds: string[];
    }) => {
      await Promise.all([
        configureRoadmapCardDisabledTargetsAdmin({
          cardKey,
          scope: "organization",
          organizationIds,
          deviceIds: [],
        }),
        configureRoadmapCardDisabledTargetsAdmin({
          cardKey,
          scope: "device",
          organizationIds: [],
          deviceIds,
        }),
      ]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getAdminRoadmapCardsQueryKey(),
      });
      await invalidateRoadmapHierarchyQueries();
      setConfigureDisabledGroup(null);
      setConfigureError(null);
    },
    onError: () => {
      setConfigureError("Failed to update disabled targets.");
    },
  });

  const buildSelectElement = (
    id: string,
    label: string,
    options: string[],
  ): SelectElement => ({
    id,
    type: "select",
    options: {
      label,
      options,
    },
  });

  const overrideScopeOptions: Array<{
    value: "organization" | "device";
    label: string;
  }> = [
      { value: "organization", label: "Organization" },
      { value: "device", label: "Device" },
    ];
  const createScopeOptions: Array<{
    value: "global" | "organization" | "device";
    label: string;
  }> = [
      { value: "global", label: "Global" },
      { value: "organization", label: "Organization" },
      { value: "device", label: "Device" },
    ];
  const createScopeLabel =
    createScopeOptions.find((option) => option.value === createScope)?.label ?? "";

  const overrideScopeLabel =
    overrideScopeOptions.find((option) => option.value === overrideScope)?.label ??
    "";

  const toggleVisibility = (key: keyof RoadmapVisibilityConditions) => {
    setVisibilityConditions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetForm = () => {
    setDialogMode("base");
    setEditingCard(null);
    setOverrideBase(null);
    setCreateScope("global");
    setCreateOrganizationId("");
    setCreateDeviceId("");
    setOverrideScope("organization");
    setOverrideOrganizationId("");
    setOverrideDeviceId("");
    setCardKey("");
    setTitle("");
    setDescription("");
    setHelpCenterArticleId("");
    setEnabled(true);
    setOrder("");
    setParentStageKey("");
    setVisibilityConditions({});
    setFormError(null);
  };

  const openBaseDialog = () => {
    resetForm();
    setDialogMode("base");
    setDialogOpen(true);
  };

  const openOverrideDialog = (baseCard: RoadmapCard) => {
    resetForm();
    setDialogMode("override");
    setOverrideBase(baseCard);
    setCardKey(baseCard.cardKey);
    setTitle(baseCard.title);
    setDescription(baseCard.description ?? "");
    setHelpCenterArticleId(baseCard.helpCenterArticleId ?? "");
    setEnabled(baseCard.enabled);
    setOrder(
      baseCard.order !== null && baseCard.order !== undefined
        ? String(baseCard.order)
        : "",
    );
    setParentStageKey(baseCard.parentStageKey);
    setVisibilityConditions(
      (baseCard.visibilityConditions as RoadmapVisibilityConditions) ?? {},
    );
    setDialogOpen(true);
  };

  const openEditDialog = (card: RoadmapCard) => {
    resetForm();
    setDialogMode("edit");
    setEditingCard(card);
    setCardKey(card.cardKey);
    setTitle(card.title);
    setDescription(card.description ?? "");
    setHelpCenterArticleId(card.helpCenterArticleId ?? "");
    setEnabled(card.enabled);
    setOrder(
      card.order !== null && card.order !== undefined ? String(card.order) : "",
    );
    setParentStageKey(card.parentStageKey);
    setVisibilityConditions(
      (card.visibilityConditions as RoadmapVisibilityConditions) ?? {},
    );
    if (card.organizationId) {
      setOverrideScope(card.deviceId ? "device" : "organization");
      setOverrideOrganizationId(card.organizationId);
    }
    if (card.deviceId) {
      setOverrideDeviceId(card.deviceId);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const openConfigureDisabledDialog = (group: CardGroup) => {
    const { organizationIds, deviceIds } = getGroupDisabledTargets(group);
    setConfigureDisabledGroup(group);
    setConfiguredOrganizationIds(organizationIds);
    setConfiguredDeviceIds(deviceIds);
    setConfigureError(null);
  };

  const handleCloseConfigureDisabledDialog = () => {
    setConfigureDisabledGroup(null);
    setConfigureError(null);
  };

  const getVisibilityPayload = () => {
    const keys = Object.keys(visibilityConditions);
    return keys.length > 0 ? visibilityConditions : null;
  };

  const handleSubmit = async () => {
    setFormError(null);

    if (!title.trim()) {
      setFormError("Title is required.");
      return;
    }

    if (dialogMode === "base" && !cardKey.trim()) {
      setFormError("Card key is required.");
      return;
    }

    if (!description.trim()) {
      setFormError("Description is required.");
      return;
    }

    if (!parentStageKey.trim()) {
      setFormError("Parent stage is required.");
      return;
    }

    if (dialogMode === "base") {
      if (createScope === "organization" && !createOrganizationId) {
        setFormError("Select an organization for this card.");
        return;
      }
      if (createScope === "device" && !resolvedCreateOrganizationId) {
        setFormError("Select a device tied to an organization.");
        return;
      }
      if (createScope === "device" && !createDeviceId) {
        setFormError("Select a device for this card.");
        return;
      }
    }

    if (dialogMode === "override") {
      if (!overrideBase) {
        setFormError("Select a base roadmap card.");
        return;
      }
      if (overrideScope === "organization" && !overrideOrganizationId) {
        setFormError("Select an organization for this override.");
        return;
      }
      if (overrideScope === "device" && !resolvedOverrideOrganizationId) {
        setFormError("Select a device tied to an organization.");
        return;
      }
      if (overrideScope === "device" && !overrideDeviceId) {
        setFormError("Select a device for this override.");
        return;
      }
    }

    const payload = {
      title: title.trim(),
      parentStageKey,
      description: description.trim(),
      enabled,
      order: order ? Number(order) : null,
      helpCenterArticleId: helpCenterArticleId.trim() || null,
      visibilityConditions: getVisibilityPayload(),
    };

    if (dialogMode === "edit" && editingCard) {
      updateMutation.mutate({
        cardId: editingCard.id,
        data: payload,
      });
      return;
    }

    if (dialogMode === "override" && overrideBase) {
      createMutation.mutate({
        ...payload,
        cardKey: overrideBase.cardKey,
        ...(overrideScope === "organization"
          ? { organizationId: overrideOrganizationId }
          : {
              organizationId: resolvedOverrideOrganizationId,
              deviceId: overrideDeviceId,
            }),
      });
      return;
    }

    createMutation.mutate({
      ...payload,
      cardKey: cardKey.trim(),
      ...(createScope === "organization"
        ? { organizationId: createOrganizationId }
        : {}),
      ...(createScope === "device"
        ? {
            organizationId: resolvedCreateOrganizationId,
            deviceId: createDeviceId,
          }
        : {}),
    });
  };

  const handleConfigureDisabledTargets = () => {
    if (!configureDisabledGroup) return;
    configureDisabledTargetsMutation.mutate({
      cardKey: configureDisabledGroup.cardKey,
      organizationIds: configuredOrganizationIds,
      deviceIds: configuredDeviceIds,
    });
  };

  useEffect(() => {
    if (dialogMode !== "base") return;
    if (createScope === "global") {
      setCreateOrganizationId("");
      setCreateDeviceId("");
    }
    if (createScope === "organization") {
      setCreateDeviceId("");
    }
    if (createScope === "device") {
      setCreateOrganizationId("");
    }
  }, [dialogMode, createScope]);

  useEffect(() => {
    if (dialogMode !== "override") return;
    if (overrideScope === "organization") {
      setOverrideDeviceId("");
    }
    if (overrideScope === "device") {
      setOverrideOrganizationId("");
    }
  }, [dialogMode, overrideScope]);

  if (!canManage) {
    return (
      <Box className="flex flex-col gap-4">
        <PageHeader
          pageTitleComponent={
            <PageBreadcrumb items={[{ label: "Admin" }, { label: "Roadmap Cards" }]} />
          }
        />
        <Typography>You do not have access to manage roadmap cards.</Typography>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col gap-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb items={[{ label: "Admin" }, { label: "Roadmap Cards" }]} />
        }
        rightButtons={
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={openBaseDialog}
          >
            New Card
          </Button>
        }
      />

      <div className="px-3 py-2 gap-4 flex flex-col">

        <AdminConfigurationNav orgId={orgId} active="roadmap-cards" />

        <Typography variant="body2" color="text.secondary">
          Create cards at global, organization, or device scope. You can also
          create overrides from a base card to customize title, placement, or
          visibility for a specific organization or device.
        </Typography>

        <TextField
          label="Search by title or key"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          fullWidth
        />

        <Stack spacing={2}>
          {filteredGroups.map((group) => (
            <Box
              key={group.cardKey}
              className="rounded-md border border-slate-200 bg-white p-4"
            >
              <Box className="flex items-center justify-between gap-3">
                <Box>
                  <Typography variant="subtitle1" className="flex items-center gap-2">
                    <LayersOutlined fontSize="small" />
                    {group.cardKey}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Disabled organizations: {getGroupDisabledTargets(group).organizationIds.length}
                    {" · "}Disabled devices: {getGroupDisabledTargets(group).deviceIds.length}
                  </Typography>
                </Box>
                <Box className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => openConfigureDisabledDialog(group)}
                  >
                    Configure Disabled
                  </Button>
                  {group.base && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openOverrideDialog(group.base!)}
                    >
                      Create Override
                    </Button>
                  )}
                </Box>
              </Box>

              <Table size="small" className="mt-3">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Stage Key</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.base && (
                    <TableRow>
                      <TableCell>{group.base.title}</TableCell>
                      <TableCell>{group.base.parentStageKey}</TableCell>
                      <TableCell>Base (Global)</TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label="Edit roadmap card"
                          onClick={() => openEditDialog(group.base!)}
                        >
                          <EditOutlined />
                        </IconButton>
                        <IconButton
                          aria-label="Delete roadmap card"
                          onClick={() => setConfirmDelete(group.base!)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {group.overrides.map((card) => (
                    <TableRow key={card.id}>
                      <TableCell>{card.title}</TableCell>
                      <TableCell>{card.parentStageKey}</TableCell>
                      <TableCell>{formatScopeLabel(card, orgNameById, deviceById)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label="Edit roadmap card"
                          onClick={() => openEditDialog(card)}
                        >
                          <EditOutlined />
                        </IconButton>
                        <IconButton
                          aria-label="Delete roadmap card"
                          onClick={() => setConfirmDelete(card)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!group.base && group.overrides.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2">
                          No cards found for this key.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          ))}
          {!isLoading && filteredGroups.length === 0 && (
            <Typography variant="body2">No roadmap cards found.</Typography>
          )}
        </Stack>
      </div>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === "edit"
            ? "Edit Roadmap Card"
            : dialogMode === "override"
              ? "Create Override"
              : "Create Roadmap Card"}
        </DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-2">
          {dialogMode === "base" && (
            <FormSelect
              element={buildSelectElement(
                "create-scope",
                "Scope",
                createScopeOptions.map((option) => option.label),
              )}
              label="Scope"
              value={createScopeLabel}
              onChange={(_, value) => {
                const selected = createScopeOptions.find(
                  (option) => option.label === value.answer,
                );
                if (selected) {
                  setCreateScope(selected.value);
                }
              }}
            />
          )}

          {dialogMode === "base" && createScope === "organization" && (
            <Autocomplete
              options={adminOrganizations}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              value={selectedCreateOrganization}
              onChange={(_, value) => {
                setCreateOrganizationId(value?.id ?? "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Organization" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          {dialogMode === "base" && createScope === "device" && (
            <Autocomplete
              options={adminDevices}
              getOptionLabel={(option) =>
                option.organizationName
                  ? `${option.organizationName} — ${option.name}`
                  : option.name
              }
              getOptionKey={(option) => option.id}
              value={selectedCreateDevice}
              onChange={(_, value) => {
                setCreateDeviceId(value?.id ?? "");
                if (value?.organizationId) {
                  setCreateOrganizationId(value.organizationId);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Device" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          {dialogMode === "override" && (
            <FormSelect
              element={buildSelectElement(
                "override-scope",
                "Override scope",
                overrideScopeOptions.map((option) => option.label),
              )}
              label="Override scope"
              value={overrideScopeLabel}
              onChange={(_, value) => {
                const selected = overrideScopeOptions.find(
                  (option) => option.label === value.answer,
                );
                if (selected) {
                  setOverrideScope(selected.value);
                }
              }}
            />
          )}

          {dialogMode === "override" && overrideScope === "organization" && (
            <Autocomplete
              options={adminOrganizations}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              value={selectedOverrideOrganization}
              onChange={(_, value) => {
                setOverrideOrganizationId(value?.id ?? "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Organization" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          {dialogMode === "override" && overrideScope === "device" && (
            <Autocomplete
              options={adminDevices}
              getOptionLabel={(option) =>
                option.organizationName
                  ? `${option.organizationName} — ${option.name}`
                  : option.name
              }
              getOptionKey={(option) => option.id}
              value={selectedOverrideDevice}
              onChange={(_, value) => {
                setOverrideDeviceId(value?.id ?? "");
                if (value?.organizationId) {
                  setOverrideOrganizationId(value.organizationId);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Device" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          <TextField
            label="Card Key"
            value={cardKey}
            onChange={(event) => setCardKey(event.target.value)}
            fullWidth
            disabled={dialogMode !== "base"}
          />

          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
          />

          <Autocomplete
            options={stageOptions}
            getOptionLabel={(option) => `${option.title} (${option.stageKey})`}
            value={selectedStageOption}
            onChange={(_, value) => setParentStageKey(value?.stageKey ?? "")}
            renderInput={(params) => (
              <TextField {...params} label="Parent Stage" />
            )}
            isOptionEqualToValue={(option, value) =>
              option.stageKey === value.stageKey
            }
          />

          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <TextField
            label="Help Center Article ID"
            value={helpCenterArticleId}
            onChange={(event) => setHelpCenterArticleId(event.target.value)}
            fullWidth
          />

          <TextField
            label="Order"
            value={order}
            onChange={(event) => setOrder(event.target.value)}
            type="number"
            fullWidth
          />

          <FormControl component="fieldset">
            <Typography variant="subtitle2">Enabled</Typography>
            <RadioGroup
              row
              value={enabled ? "true" : "false"}
              onChange={(event) => setEnabled(event.target.value === "true")}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" className="mb-2">
              Visibility conditions
            </Typography>
            <FormGroup className="grid grid-cols-2 gap-x-4">
              {VISIBILITY_CONDITION_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.key}
                  control={
                    <Checkbox
                      checked={visibilityConditions[option.key] === true}
                      onChange={() => toggleVisibility(option.key)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Box>

          {formError && <Typography color="error">{formError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            {dialogMode === "edit" ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!configureDisabledGroup}
        onClose={handleCloseConfigureDisabledDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configure Disabled Targets</DialogTitle>
        <DialogContent className="flex flex-col gap-3 pt-2">
          <Typography variant="body2" color="text.secondary">
            Select organizations and devices where this roadmap card should be hidden.
          </Typography>
          <Autocomplete
            multiple
            options={adminOrganizations}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            value={selectedConfiguredOrganizations}
            renderTags={() => null}
            onChange={(_, value) =>
              setConfiguredOrganizationIds(value.map((organization) => organization.id))
            }
            renderInput={(params) => (
              <TextField {...params} label="Disabled organizations" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Box className="flex flex-wrap gap-1">
            {selectedConfiguredOrganizations.map((organization) => (
              <Chip
                key={organization.id}
                label={organization.name}
                size="small"
                onDelete={() =>
                  setConfiguredOrganizationIds((prev) =>
                    prev.filter((id) => id !== organization.id),
                  )
                }
              />
            ))}
          </Box>
          <Autocomplete
            multiple
            options={adminDevices}
            getOptionLabel={(option) =>
              option.organizationName
                ? `${option.organizationName} — ${option.name}`
                : option.name
            }
            getOptionKey={(option) => option.id}
            value={selectedConfiguredDevices}
            renderTags={() => null}
            onChange={(_, value) =>
              setConfiguredDeviceIds(value.map((device) => device.id))
            }
            renderInput={(params) => (
              <TextField {...params} label="Disabled devices" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Box className="flex flex-wrap gap-1">
            {selectedConfiguredDevices.map((device) => (
              <Chip
                key={device.id}
                label={
                  device.organizationName
                    ? `${device.organizationName} — ${device.name}`
                    : device.name
                }
                size="small"
                onDelete={() =>
                  setConfiguredDeviceIds((prev) =>
                    prev.filter((id) => id !== device.id),
                  )
                }
              />
            ))}
          </Box>
          {configureError && <Typography color="error">{configureError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfigureDisabledDialog} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfigureDisabledTargets}
            disabled={configureDisabledTargetsMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete roadmap card?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (confirmDelete) {
                deleteMutation.mutate(confirmDelete.id);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
